import {
  PaginatedRequest,
  PaginatedResponse,
  ValidationErrorResponse,
} from '@opositandopn/http';
import { useToasterActions } from '@opositandopn/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';

import { useAxios } from '../../../context/AxiosContext';
import {
  AssessmentViewModel,
  SubjectViewModel,
} from '../../questions/api/useMultipleChoiceQuestions';

export type Status = 'open' | 'closed' | 'resolved';

export type Format =
  | 'multiple_choice'
  | 'true_false'
  | 'likert'
  | 'answer'
  | 'fill_the_gap';

export interface UserViewModel {
  username?: string;
  email: string;
  avatarUrl?: string;
  id: string;
  name?: string;
}

export interface ErrorReportViewModel {
  id: string;
  createdAt: Date;
  subject: SubjectViewModel;
  questionId: string;
  format: Format;
  message: string;
  status: Status;
  user: UserViewModel;
  assessments?: AssessmentViewModel[];
}

export type GetErrorReportsRequest = PaginatedRequest & {
  subjectId: string;
  params?: {
    fromTime: Date;
    toTime: Date;
    text?: string;
    statuses?: Status[];
  };
};

export const useGetErrorReports = (request: GetErrorReportsRequest) => {
  const { instance } = useAxios();

  async function queryFn(): Promise<PaginatedResponse<ErrorReportViewModel>> {
    const response = await instance.get<
      PaginatedResponse<ErrorReportViewModel>
    >(`/v2/subjects/${request.subjectId}/reports`, {
      params: {
        fromTime: request.params?.fromTime,
        toTime: request.params?.toTime,
        pageNumber: request.pageNumber,
        pageSize: request.pageSize,
        sort: request.sort,
        text: request.params?.text,
        status: request.params?.statuses,
      },
      paramsSerializer: {
        indexes: null,
      },
    });

    return response.data;
  }

  return useQuery({
    queryKey: ['getErrorReports', request],
    queryFn,
  });
};

export interface UpdateErrorReportRequest {
  id: string;
  subjectId: string;
  status: Status;
}

export const useUpdateErrorReport = () => {
  const { instance } = useAxios();
  const queryClient = useQueryClient();
  const { sendMessage } = useToasterActions();

  async function mutationFn(request: UpdateErrorReportRequest) {
    const response = await instance.patch(
      `/v2/subjects/${request.subjectId}/reports/${request.id}`,
      {
        status: request.status,
      },
    );

    return response.data;
  }

  return useMutation<
    void,
    AxiosError<ValidationErrorResponse>,
    UpdateErrorReportRequest
  >({
    mutationFn,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['getErrorReport'],
      });
      queryClient.invalidateQueries({
        queryKey: ['getErrorReports'],
      });

      sendMessage('¡Hecho!', 'Reporte actualizado', 'success');
    },
    meta: {
      errorMessages: {
        invalid_operation: { title: 'Operación inválida' },
      },
    },
  });
};
